import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  DocumentTypeState,
  initialDocumentTypeState
} from "./document-type.state";
import {
  DocumentTypeService,
  DocumentTypeResponse
} from "../../services/document-type.service";

// Async thunk to fetch document types from API
export const fetchDocumentTypes = createAsyncThunk(
  "documentType/fetchDocumentTypes",
  async (_, { rejectWithValue }) => {
    try {
      const result = await DocumentTypeService.getAllDocumentTypes();

      if (result.error) {
        return rejectWithValue(
          result.error.data || ["Failed to fetch document types"]
        );
      }

      if (!result.data) {
        return rejectWithValue(["No data received from API"]);
      }

      // Filter active and non-deleted document types
      const activeDocTypes = result.data.filter(
        (doc: DocumentTypeResponse) => doc.is_active && !doc.is_deleted
      );

      // Sort by created_on date in descending order (latest first)
      // const sortedDocTypes = activeDocTypes.sort((a, b) => {
      //   const dateA = new Date(a.created_on).getTime();
      //   const dateB = new Date(b.created_on).getTime();
      //   return dateB - dateA; // Descending order (latest first)
      // });

      // Map to dropdown options
      const options = activeDocTypes.map((doc: DocumentTypeResponse) => ({
        value: doc.id.toString(),
        label: doc.name
      }));

      return options;
    } catch (error: any) {
      return rejectWithValue([
        error.message || "Failed to fetch document types"
      ]);
    }
  }
);

const documentTypeSlice = createSlice({
  name: "documentType",
  initialState: initialDocumentTypeState,
  reducers: {
    setSelectedDocumentType: (state, action: PayloadAction<string>) => {
      state.selectedDocumentType = action.payload;
      // Persist to sessionStorage
      try {
        sessionStorage.setItem("selectedDocumentType", action.payload);
      } catch (error) {
        console.error("Error saving document type to sessionStorage:", error);
      }
    },
    resetDocumentType: (state) => {
      state.selectedDocumentType =
        initialDocumentTypeState.selectedDocumentType;
      // Clear from sessionStorage
      try {
        sessionStorage.removeItem("selectedDocumentType");
      } catch (error) {
        console.error(
          "Error removing document type from sessionStorage:",
          error
        );
      }
    },
    setDocumentTypeOptions: (
      state,
      action: PayloadAction<Array<{ value: string; label: string }>>
    ) => {
      state.documentTypeOptions = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocumentTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.documentTypeOptions = action.payload;
        state.error = null;
      })
      .addCase(fetchDocumentTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string[]) || [
          "Failed to fetch document types"
        ];
      });
  }
});

export const {
  setSelectedDocumentType,
  resetDocumentType,
  setDocumentTypeOptions
} = documentTypeSlice.actions;
export default documentTypeSlice.reducer;
export type { DocumentTypeState };
